import React, { useState } from 'react';
import classNames from 'classnames';

import iconExpandDown from '../../assets/img/icon-chevron.svg';
import { useIsMobile } from '../../hooks';
import { ClickableDiv, ClickableImage, ClickableTr } from '..';

import styles from './FinancialsTableRow.module.scss';

type Props = {
  data: Array<Array<number | string>>;
  isClickable?: boolean;
  isCashflow?: boolean;
  bigLine?: boolean;
  currentPeriod?: number;
  children?: any;
  isMaxNestLevel?: boolean;
  isBaseFontRow?: boolean;
};

const FinancialsTableRow = (props: Props) => {
  const { data, isClickable, isCashflow, bigLine, currentPeriod, children, isMaxNestLevel, isBaseFontRow } = props;
  const { isMobile } = useIsMobile();

  const spacedIndexes = [5, 6, 7, 8];
  const [isSelectTabOpened, setIsSelectTabOpened] = useState(false);

  const formatNumber = (num: number | string, index?: number) => {
    if (num === undefined || num === null) return '-';
    if (typeof num === 'string') return num;
    if ((num as number).toString().length > 4 && num.toString().includes('.')) return (num as number).toFixed(2);
    if (index && index % 2 !== 0) return '';
    if (num <= 1000 && num >= -1000) return num;
    const operator = num < 0 ? '-' : '';
    if (Math.abs(num) >= 1000000000) return `${operator}${(Math.abs(num) / 1000000000).toFixed(1)}b`;
    if (Math.abs(num) >= 1000000) return `${operator}${(Math.abs(num) / 1000000).toFixed(1)}m`;
    return `${operator}${(Math.abs(num) / 1000).toFixed(0)}k`;
  };

  return (
    <>
      {children ? (
        <>
          {isMobile && currentPeriod ? (
            <>
              <ClickableDiv
                className={classNames(
                  styles.mobileMainRow,
                  isBaseFontRow && styles.mobileBaseFont,
                )}
                onClick={() => setIsSelectTabOpened(!isSelectTabOpened)}
              >
                <div className={styles.key}>
                  <ClickableImage
                    alt=""
                    src={iconExpandDown}
                    className={isSelectTabOpened ? styles.iconShowLessMobile : styles.iconExpandMobile}
                  />
                  <div className={styles.subKey}>
                    {data[0][0]}
                  </div>
                </div>
                <div className={styles.value}>
                  {formatNumber(data[0][currentPeriod])}
                </div>
              </ClickableDiv>
              {isSelectTabOpened && children}
            </>
          ) : (
            <>
              {bigLine && (
                <div className={styles.bigLine} />
              )}
              <ClickableTr
                className={bigLine ? styles.bigRow : styles.row}
                onClick={() => setIsSelectTabOpened(!isSelectTabOpened)}
              >
                {data[0].map((item, index) => (
                  <td key={item[0]} className={styles.item}>
                    {index === 0 && (
                      <ClickableImage
                        alt=""
                        src={iconExpandDown}
                        className={isSelectTabOpened ? styles.iconShowLess : styles.iconExpand}
                      />
                    )}
                    {formatNumber(item)}
                  </td>
                ))}
              </ClickableTr>
              {isSelectTabOpened && children}
            </>
          )}
        </>
      ) : (
        <>
          {isMobile && currentPeriod ? (
            <>
              <ClickableDiv
                className={classNames(
                  styles.mobileMainRow,
                  isBaseFontRow && styles.mobileBaseFont,
                )}
                onClick={() => setIsSelectTabOpened(!isSelectTabOpened)}
              >
                <div className={styles.key}>
                  {isClickable && (
                    <ClickableImage
                      alt=""
                      src={iconExpandDown}
                      className={isSelectTabOpened ? styles.iconShowLessMobile : styles.iconExpandMobile}
                    />
                  )}
                  <div className={styles.subKey}>
                    {data[0][0]}
                  </div>
                </div>
                <div className={styles.value}>
                  {formatNumber(data[0][currentPeriod])}
                </div>
              </ClickableDiv>
              {isSelectTabOpened
                && data.slice(1).map((row, index) => (
                  <div
                    key={row[0]}
                    className={styles.mobileRowSecondary}
                  >
                    <div className={classNames(styles.key, isMaxNestLevel ? styles.lastLevelKey : styles.secondaryKey)}>
                      {row[0]}
                    </div>
                    <div className={styles.value}>{formatNumber(row[currentPeriod])}</div>
                  </div>
                ))}
            </>
          ) : (
            <>
              {isClickable ? (
                <>
                  {bigLine && (
                    <div className={styles.bigLine} />
                  )}
                  <ClickableTr
                    className={bigLine ? styles.bigRow : styles.row}
                    onClick={() => setIsSelectTabOpened(!isSelectTabOpened)}
                  >
                    {data[0].map((item, index) => (
                      <td key={item[0]} className={styles.item}>
                        {index === 0 && (
                          <ClickableImage
                            alt=""
                            src={iconExpandDown}
                            className={isSelectTabOpened ? styles.iconShowLess : styles.iconExpand}
                          />
                        )}
                        {formatNumber(item)}
                      </td>
                    ))}
                  </ClickableTr>
                  {isSelectTabOpened && data.slice(1).map((row, rowIndex) => (
                    <tr key={row[0]} className={styles.row}>
                      {row.map((item, index) => (
                        <td className={styles.subItem}>
                          <div
                            className={classNames(
                              index === 0 ? styles.firstSubChild : styles.subChild,
                              isCashflow && spacedIndexes.includes(rowIndex) && index === 0 && styles.itemSpace,
                            )}
                          >
                            {formatNumber(item)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              ) : (
                <>
                  {data.map((row) => (
                    <>
                      {bigLine && (
                        <div className={styles.bigLine} />
                      )}
                      <tr className={styles.row}>
                        {row.map((item) => (
                          <td className={styles.item}>
                            <div>
                              {formatNumber(item)}
                            </div>
                          </td>
                        ))}
                      </tr>
                    </>
                  ))}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default FinancialsTableRow;
